/**
 * Coding agents on or off for this hub (SchedulerSettings.agents.off). Orgus starts no session
 * of an agent that is off: placement and failover skip it, a mission pinned to it waits, and the
 * hub refuses a start that names it. Running sessions keep going. Takes effect now: a switch.
 * The assistant runs on Claude Code, so it stops while that is off; AI jobs move to Codex or Gemini CLI.
 * An agent failing on a machine (state.health, cooling) shows why, under its switch, and so does
 * a machine whose own ceiling refuses it (state.refused): Orgus never places it there.
 * Save credits (agents.spare): Orgus gives that agent only large work, or work no other agent
 * can take, so a scarce subscription lasts.
 * Subscription usage (state.usage, module 'agent-usage'): each agent's windows on each machine,
 * under its switch (AgentUsageRows), and the switch for Claude Code's /usage check.
 * Environment (state.environment, module 'agent-environment', ADR 0093): one line per agent,
 * whether its sessions find the same tools as the owner's terminal; the differences in its tooltip.
 */
import { useMemo, useState } from 'react';
import { FAILURE_CAUSE_LABEL, KNOWN_AGENT_KINDS, agentKindLabel, environmentParityLine, offeredAgentKinds } from '@orgus/protocol';
import type { AgentHealth, Machine, SchedulerState } from '@orgus/protocol';
import { Checkbox, ErrorState, Label, Panel, Switch, formatClock, formatDuration, toast } from '@orgus/ui';
import { CircleCheck, ShieldBan, TriangleAlert } from '@orgus/ui/icons';
import { toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { AgentHealthRows } from './AgentHealthRows.tsx';
import { AgentUsageRows } from './AgentUsageRows.tsx';
import { patchSettings, setUsageProbe, testAgent } from './data.ts';

/** Whether an agent's sessions resolve the same tools as the owner's terminal (ADR 0093); nothing before a report. */
export function EnvironmentLine({ state, kind, machineName }: { state: SchedulerState; kind: string; machineName: (id: string) => string }) {
  const line = environmentParityLine(state.environment ?? [], kind, machineName);
  if (!line) return null;
  const Icon = line.tone === 'ok' ? CircleCheck : TriangleAlert;
  return (
    <span className={`flex items-center gap-1 text-caption ${line.tone === 'ok' ? 'text-fg-subtle' : 'text-status-attention'}`} title={line.detail ?? undefined}>
      <Icon className="size-3" aria-hidden />
      {line.text}
    </span>
  );
}

/** Always listed, so an agent can be turned off before a machine reports it. */
const ALWAYS = [...KNOWN_AGENT_KINDS, 'gemini-cli'];

export function AgentSwitches({ state, machines }: { state: SchedulerState; machines: Machine[] }) {
  const off = state.settings.agents?.off ?? [];
  const spare = state.settings.agents?.spare ?? [];
  const kinds = useMemo(() => [...new Set([...ALWAYS, ...machines.flatMap((m) => offeredAgentKinds(m, m.labels)), ...off])], [machines, off]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const allOff = kinds.every((k) => off.includes(k));
  const now = Date.now();
  const failing = (kind: string): AgentHealth | null => (state.health ?? []).filter((h) => h.kind === kind && h.coolUntil > now).sort((a, b) => b.at - a.at)[0] ?? null;
  const machineName = (id: string) => machines.find((m) => m.id === id)?.name ?? 'a machine';
  /** Machines whose own ceiling refuses this kind (state.refused): Orgus never places it there. */
  const refusedOn = (kind: string) => (state.refused ?? []).filter((r) => r.kind === kind);
  const usageOf = (kind: string) => (state.usage ?? []).filter((u) => u.agentKind === kind);
  const usageMachines = new Set((state.usage ?? []).map((u) => u.machineId)).size;
  const breakerOn = state.settings.runtimeBreaker === true;

  async function probe(on: boolean) {
    setBusy('claude-code');
    setError(null);
    try {
      await setUsageProbe(on);
      toast.success(on ? 'Checking Claude Code usage' : 'Usage checks stopped', on ? 'Each machine runs /usage every 15 minutes, so routing sees how much is left.' : 'Orgus still reads the limits that runs it starts report.');
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function flip(kind: string, on: boolean) {
    setBusy(kind);
    setError(null);
    try {
      await patchSettings({ agents: { off: on ? off.filter((k) => k !== kind) : [...off, kind] } });
      const label = agentKindLabel(kind);
      toast.success(on ? `${label} turned on` : `${label} turned off`, on ? 'Orgus can start it again.' : 'Orgus starts no new sessions of it. Running ones keep going.');
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function test(machineId: string, kind: string) {
    setBusy(kind);
    setError(null);
    try {
      await testAgent(machineId, kind);
      toast.success(`Testing ${agentKindLabel(kind)}`, `${machineName(machineId)} runs it in a throwaway repository. The result shows here.`);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function setBreaker(on: boolean) {
    setBusy('breaker');
    setError(null);
    try {
      await patchSettings({ runtimeBreaker: on });
      toast.success(on ? 'Broken agents stop' : 'Broken agents keep running', on ? 'A broken runtime stops that agent on its machine until a self-test passes.' : 'Orgus records a broken runtime but keeps using the agent.');
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  }

  async function save(kind: string, on: boolean) {
    setBusy(kind);
    setError(null);
    try {
      await patchSettings({ agents: { spare: on ? [...spare, kind] : spare.filter((k) => k !== kind) } });
      const label = agentKindLabel(kind);
      toast.success(on ? `Saving ${label} credits` : `${label} takes any work again`, on ? 'Other agents go first. It gets large work, or work no other agent can take.' : 'Orgus picks it like any other agent.');
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Coding agents">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap gap-x-6 gap-y-3">
          {kinds.map((k) => {
            const h = off.includes(k) ? null : failing(k);
            const refused = off.includes(k) ? [] : refusedOn(k);
            return (
              <div key={k} className="flex flex-col gap-1">
                <Label className="gap-3 text-ui text-fg">
                  <Switch checked={!off.includes(k)} onCheckedChange={(v) => void flip(k, v)} disabled={busy !== null} aria-label={`${agentKindLabel(k)} on`} />
                  {agentKindLabel(k)}
                </Label>
                {!off.includes(k) && <Checkbox checked={spare.includes(k)} onCheckedChange={(v) => void save(k, v)} disabled={busy !== null} label="Save credits" aria-label={`Save ${agentKindLabel(k)} credits`} />}
                {refused.length > 0 && (
                  <span className="flex items-center gap-1 text-caption text-fg-subtle" title={`Allow it there with orgus agent ceiling allow ${refused[0]!.entry === '*' ? "'*'" : refused[0]!.entry}.`}>
                    <ShieldBan className="size-3" aria-hidden />
                    Refused on {refused.map((r) => machineName(r.machineId)).join(', ')} (its ceiling)
                  </span>
                )}
                {h && (
                  <span className="flex items-center gap-1 text-caption text-status-attention" title={h.lastMessage}>
                    <TriangleAlert className="size-3" aria-hidden />
                    {FAILURE_CAUSE_LABEL[h.lastCause]} on {machineName(h.machineId)}, skipped until {formatClock(h.coolUntil)}
                  </span>
                )}
                {!off.includes(k) && (
                  <>
                    <EnvironmentLine state={state} kind={k} machineName={machineName} />
                    <AgentHealthRows
                      kind={k}
                      machines={machines.filter((m) => offeredAgentKinds(m, m.labels).includes(k))}
                      breakers={state.breakers ?? []}
                      selfTests={state.selfTests ?? []}
                      showHealthy={breakerOn}
                      busy={busy !== null}
                      onTest={(m) => void test(m, k)}
                      onTurnOff={() => void flip(k, false)}
                    />
                  </>
                )}
                {usageOf(k).map((u) => (
                  <AgentUsageRows key={u.machineId} usage={u} machineName={usageMachines > 1 ? machineName(u.machineId) : null} now={now} />
                ))}
                {k === 'claude-code' && state.usageProbe !== undefined && !off.includes(k) && (
                  <Checkbox checked={state.usageProbe} onCheckedChange={(v) => void probe(v)} disabled={busy !== null} label="Check usage every 15 min" aria-label="Check Claude Code usage every 15 minutes" />
                )}
              </div>
            );
          })}
        </div>
        <p className="text-caption text-fg-subtle">
          {allOff ? 'Every agent is off, so Orgus starts no new sessions. Turn one on to run missions.' : 'Orgus starts only the agents that are on, for missions and new sessions. Running sessions keep going.'}
          {off.includes('claude-code') && !allOff ? ' The assistant runs on Claude Code, so it is paused while that is off. AI jobs run on another agent.' : ''}
          {spare.length > 0 && !allOff ? ` Saving credits: other agents go first; ${spare.map(agentKindLabel).join(', ')} gets large work or what no other agent can take.` : ''}
        </p>
        <Checkbox checked={breakerOn} onCheckedChange={(v) => void setBreaker(v)} disabled={busy !== null} label="Stop an agent whose runtime breaks until a self-test passes" aria-label="Stop an agent whose runtime breaks until a self-test passes" />
        {(state.frozen ?? []).map((f) => (
          <span key={f.machineId} className="flex items-center gap-1 text-caption text-status-attention">
            <TriangleAlert className="size-3" aria-hidden />
            Orgus on {machineName(f.machineId)} froze for {formatDuration(f.maxMs)} at {formatClock(f.at)}. That is the machine, not an agent.
          </span>
        ))}
        {error && <ErrorState variant="inline" error={error} />}
      </div>
    </Panel>
  );
}
